/**
 * Framework fragments — conditional system prompt sections injected by the
 * agent renderer at install time.
 *
 * Each fragment explains a specific aspect of the agent framework to the agent.
 * Fragments are conditional (return null when context is empty) and overridable
 * per project via skaile.yaml `agent.fragments`.
 */

import { existsSync, readFileSync } from "node:fs";
import { resolve } from "node:path";

// ── Types ─────────────────────────────────────────────────────────────────────

/**
 * A skill installed into the agent's ability directory, referenced in the system prompt.
 *
 * @docLink packages/asset-manager/api-reference#fragments
 */
export interface AbilityRef {
  /** Skill name used as the slash command (e.g. `"code-review"`). */
  name: string;
  /** One-line description shown in the `## Installed Abilities` section. */
  description: string;
}

/**
 * A contract file installed alongside skills, referenced by path in the system prompt.
 *
 * @docLink packages/asset-manager/api-reference#fragments
 */
export interface ContractRef {
  /** Contract name for display purposes. */
  name: string;
  /** Path relative to workspace root, e.g. `".claude/skills/iron-laws/CONTRACT.md"`. */
  relativePath: string;
}

/**
 * A data connector declared in `skaile.yaml` resources.
 *
 * @docLink packages/asset-manager/api-reference#fragments
 */
export interface ConnectorRef {
  /** Connector ID as declared in `skaile.yaml`. */
  id: string;
  /** Connector driver type (e.g. `"postgres"`, `"redis"`, `"sqlite"`). */
  driver: string;
  /** Declared access level (`"read-only"` or `"read-write"`). */
  access: string;
}

/**
 * Context passed to each fragment function at render time.
 *
 * @docLink packages/asset-manager/api-reference#fragments
 */
export interface FragmentContext {
  /** Agent framework ID (e.g. `"claude-code"`, `"omp"`, `"codex"`). */
  framework: string;
  /** Relative path to the framework's skill/ability directory, e.g. `".claude/skills"`. */
  skillsDir: string;
  /** Relative path to the framework's agent directory, e.g. `".claude/agents"`. */
  agentsDir: string;
  /** Sub-agent names from `manifest.requires[]`. */
  subAgents: string[];
  /** Connectors declared in `skaile.yaml`. */
  connectors: ConnectorRef[];
  /** Resolved ability refs (installed skills). */
  abilityRefs: AbilityRef[];
  /** Resolved contract refs (installed alongside skills). */
  contractRefs: ContractRef[];
}

/**
 * Identifier for a built-in system prompt fragment.
 * - `"agent-mode"` — describes whether the agent is a sole agent or an orchestrator.
 * - `"handoff"` — delegation guidelines injected when sub-agents are declared.
 * - `"skill-discovery"` — skill directory location injected when abilities are installed.
 * - `"connector-usage"` — connected resources list injected when connectors are declared.
 *
 * @docLink packages/asset-manager/api-reference#fragments
 */
export type FragmentId = "agent-mode" | "handoff" | "skill-discovery" | "connector-usage";

// ── Built-in fragments ────────────────────────────────────────────────────────

function agentModeFragment(ctx: FragmentContext): string | null {
  if (ctx.subAgents.length === 0) {
    return [
      "## Operating Mode",
      "",
      "You are the sole agent for this workspace. Complete tasks autonomously",
      "using your installed abilities and connected resources.",
    ].join("\n");
  }

  const list = ctx.subAgents.map((n) => `- \`${n}\``).join("\n");
  return [
    "## Operating Mode",
    "",
    "You are the orchestrator for this workspace. Coordinate specialized",
    "sub-agents when tasks fall within their expertise:",
    "",
    list,
    "",
    "Delegate clearly: describe the task, expected output format, and any",
    "constraints. Synthesize results before presenting to the user.",
  ].join("\n");
}

function handoffFragment(ctx: FragmentContext): string | null {
  if (ctx.subAgents.length === 0) return null;

  const lines = [
    "## Delegation",
    "",
    "When delegating to a sub-agent:",
    "1. State the specific task and its acceptance criteria.",
    "2. Provide relevant context the sub-agent cannot infer on its own.",
    "3. Specify the expected output format.",
    "4. If the result is incomplete, refine and retry once before escalating.",
  ];

  if (ctx.framework === "claude-code") {
    lines.push("", `Use the Agent tool to dispatch. Sub-agents are in \`${ctx.agentsDir}/\`.`);
  }

  return lines.join("\n");
}

function skillDiscoveryFragment(ctx: FragmentContext): string | null {
  if (ctx.abilityRefs.length === 0 && ctx.contractRefs.length === 0) return null;

  const lines = [
    "## Skills and Abilities",
    "",
    `Your specialized abilities are installed in \`${ctx.skillsDir}/\`.`,
    "Read a skill's SKILL.md before executing a task that matches its purpose.",
  ];

  if (ctx.framework === "claude-code") {
    lines.push("Abilities are also available as slash commands.");
  }

  if (ctx.contractRefs.length > 0) {
    lines.push(
      "",
      `Operational contracts are co-located in \`${ctx.skillsDir}/\`. Read the`,
      "relevant CONTRACT.md before acting on any constrained task.",
    );
  }

  return lines.join("\n");
}

function connectorUsageFragment(ctx: FragmentContext): string | null {
  if (ctx.connectors.length === 0) return null;

  const rows = ctx.connectors.map((c) => `- \`${c.id}\` (${c.driver}, ${c.access})`);

  return [
    "## Connected Resources",
    "",
    "The following data sources are connected to this workspace:",
    "",
    ...rows,
    "",
    "Access via `skaile res <id> <operation>` or the registered tool functions.",
    "Respect the declared access level — read-only resources must not be written to.",
  ].join("\n");
}

/**
 * Registry of all built-in fragment functions keyed by {@link FragmentId}.
 *
 * Each function receives a {@link FragmentContext} and returns the fragment markdown
 * to inject, or `null` when the fragment's condition is not met (e.g. `"handoff"`
 * returns `null` when there are no sub-agents).
 *
 * @docLink packages/asset-manager/api-reference#fragments
 */
export const BUILT_IN_FRAGMENTS: Record<FragmentId, (ctx: FragmentContext) => string | null> = {
  "agent-mode": agentModeFragment,
  handoff: handoffFragment,
  "skill-discovery": skillDiscoveryFragment,
  "connector-usage": connectorUsageFragment,
};

// ── Resolution ────────────────────────────────────────────────────────────────

/**
 * Resolve all framework fragments for the rendered system prompt.
 *
 * Applies per-project overrides from `skaile.yaml` `agent.fragments`:
 * - `true` / absent → include built-in fragment (default)
 * - `false` → omit fragment
 * - `string` → load markdown from path relative to `workspaceRoot`
 *
 * @param ctx - Fragment context built from the agent manifest and workspace config.
 * @param overrides - Per-project overrides map from `skaile.yaml` `agent.fragments`.
 * @param workspaceRoot - Absolute path to the workspace root (for resolving custom fragment paths).
 * @returns Ordered list of resolved fragment markdown strings to inject into the system prompt.
 * @docLink packages/asset-manager/api-reference#fragments
 */
export function resolveFragments(
  ctx: FragmentContext,
  overrides: Record<string, boolean | string> | undefined,
  workspaceRoot: string,
): string[] {
  const results: string[] = [];

  for (const id of [
    "agent-mode",
    "handoff",
    "skill-discovery",
    "connector-usage",
  ] as FragmentId[]) {
    const override = overrides?.[id];

    if (override === false) continue;

    if (typeof override === "string") {
      const filePath = resolve(workspaceRoot, override);
      if (existsSync(filePath)) {
        const content = readFileSync(filePath, "utf-8").trim();
        if (content) results.push(content);
      }
      continue;
    }

    // true or absent — use built-in
    const fragment = BUILT_IN_FRAGMENTS[id](ctx);
    if (fragment) results.push(fragment);
  }

  return results;
}

/**
 * Load prompt-extension files declared under `skaile.yaml` `agent.prompt-extensions`.
 *
 * Paths are relative to `workspaceRoot`. Missing files are silently skipped.
 *
 * @param paths - Array of relative file paths from `skaile.yaml` `agent.prompt-extensions`.
 * @param workspaceRoot - Absolute path to the workspace root.
 * @returns Non-empty content strings for each file that exists and is non-empty.
 * @docLink packages/asset-manager/api-reference#fragments
 */
export function loadPromptExtensions(paths: string[] | undefined, workspaceRoot: string): string[] {
  if (!paths?.length) return [];
  return paths
    .map((p) => {
      const filePath = resolve(workspaceRoot, p);
      if (!existsSync(filePath)) return null;
      return readFileSync(filePath, "utf-8").trim();
    })
    .filter((s): s is string => s !== null && s.length > 0);
}
